import React, { useEffect, useRef } from 'react';

// Hooks
import { useNavigate } from 'react-router-dom';
import { isSystemAdmin } from '~/hooks/permissions';

// Utils
import { clearStorage } from '~/utils/local-storage';
import { setDashboardMode } from '~/utils/auth';

// Icons
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';

// Mui Component
import { Box, Typography, Avatar, Tooltip, Divider, Skeleton } from '@mui/material';

// Styles
import './styles.scss';

const index = (props) => {
  const dropDownRef = useRef(null);
  const { setShowDropDown, dropValue, userName, userEmail, isAdminMode, firstName, lastName } =
    props;
  const navigate = useNavigate();

  const handleAfterClick = (item) => {
    if (typeof item.beforeNavigate === 'function') item.beforeNavigate();
    navigate(item.path);
    setShowDropDown(false);
  };

  const handleProfileClick = () => {
    navigate('/account/profile');
    setShowDropDown(false);
  };

  useEffect(() => {
    let counter = 0;
    const handleClickOutside = (event) => {
      if (counter == 1 && dropDownRef.current && !dropDownRef.current.contains(event.target)) {
        setShowDropDown(false);
      } else {
        counter = 1;
      }
    };

    if (dropValue == true) {
      window.addEventListener('click', handleClickOutside);
    }

    return () => {
      window.removeEventListener('click', handleClickOutside);
    };
  }, [dropValue]);

  const userPanelItems = [
    {
      id: 'company-settings',
      name: 'Company Settings',
      path: '/my-company',
      Icon: BusinessOutlinedIcon,
    },
    {
      id: 'manage-users',
      name: 'Manage Users',
      path: '/account/users',
      Icon: GroupOutlinedIcon,
    },
    {
      id: 'engagement',
      name: 'Engagement',
      path: '/phone-tracking',
      Icon: SyncAltOutlinedIcon,
    },
    {
      id: 'billing-plans',
      name: 'Billing & Plans',
      path: '/account/wallet',
      Icon: CreditCardOutlinedIcon,
    },
    {
      id: 'integrations',
      name: 'Integrations',
      path: '/settings/api-keys',
      Icon: ExtensionOutlinedIcon,
    },
    ...(isSystemAdmin()
      ? [
          {
            id: 'admin-portal',
            name: 'Admin Portal',
            path: '/admin/overview',
            Icon: AdminPanelSettingsOutlinedIcon,
            beforeNavigate: () => setDashboardMode(true),
          },
        ]
      : []),
  ];

  const adminPanelItems = [
    {
      id: 'back-to-account',
      name: 'Back to Account',
      path: '/overview',
      Icon: BusinessOutlinedIcon,
      beforeNavigate: () => setDashboardMode(false),
    },
  ];

  const logoutItem = {
    id: 'logout',
    name: 'Logout',
    path: '/login',
    Icon: LogoutOutlinedIcon,
    beforeNavigate: clearStorage,
  };

  const primaryItems = isAdminMode ? adminPanelItems : userPanelItems;

  return (
    <>
      <Box ref={dropDownRef} className="newDropDownWrapper headerMenuSurface">
        <Box
          className="UserInfoSection"
          role="button"
          tabIndex={0}
          aria-label="Open profile"
          onClick={handleProfileClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleProfileClick();
            }
          }}
        >
          {userName ? (
            <Avatar className="userAvatar">{(firstName?.[0] || '') + (lastName?.[0] || '')}</Avatar>
          ) : (
            <Skeleton variant="circular" width={40} height={40} />
          )}
          <Box className="userInfo">
            {userName ? (
              <Typography className="userName">
                {userName.length > 20 ? (
                  <Tooltip
                    title={userName}
                    placement="bottom"
                    disableHoverListener={userName.length < 20}
                  >
                    {userName.length > 20 ? `${userName.slice(0, 15)}...` : userName}
                  </Tooltip>
                ) : (
                  userName
                )}
              </Typography>
            ) : (
              <Skeleton variant="text" width={100} height={20} />
            )}
            {userEmail ? (
              <Typography className="userEmail">
                {userEmail.length > 20 ? `${userEmail.slice(0, 20)}...` : userEmail}
              </Typography>
            ) : (
              <Skeleton variant="text" width={150} height={20} />
            )}
          </Box>
        </Box>

        <Divider className="dropDownDivider" />
        <Box className="dropDownItems">
          {primaryItems.map((item) => (
            <button
              className="dropDownItem headerMenuItem"
              key={item.id}
              onClick={() => handleAfterClick(item)}
              type="button"
            >
              <div className="imgOuter">
                <item.Icon aria-hidden="true" />
              </div>
              <Typography>{item.name}</Typography>
            </button>
          ))}
        </Box>

        <Divider className="dropDownDivider" />
        <Box className="dropDownItems">
          <button
            className="dropDownItem headerMenuItem"
            onClick={() => handleAfterClick(logoutItem)}
            type="button"
          >
            <div className="imgOuter">
              <logoutItem.Icon aria-hidden="true" />
            </div>
            <Typography>{logoutItem.name}</Typography>
          </button>
        </Box>
      </Box>
    </>
  );
};

export default index;
